<template>
  <StepShell
    :title="timedOut ? '' : (ready ? '' : t('completeV2.steps.liftOff.screenTitle'))"
    :intro="timedOut ? '' : (ready ? '' : t('completeV2.steps.liftOff.screenIntro'))"
    :can-go-back="false"
    :hide-footer="true"
    @next="$emit('next')"
  >
    <div
      class="lift-off"
      :class="{ 'lift-off--ready': ready, 'lift-off--timeout': timedOut }"
    >
      <!-- ── Liste des stages (tant qu'on attend) ── -->
      <ul
        v-if="!ready && !timedOut"
        class="lift-off__stages"
      >
        <li
          v-for="(stage, i) in stages"
          :key="stage.id"
          class="lift-off__stage"
          :class="`lift-off__stage--${stageStatus(i)}`"
        >
          <span class="lift-off__bullet">
            <i
              v-if="stageStatus(i) === 'done'"
              class="far fa-check"
            />
            <i
              v-else
              :class="stage.icon"
            />
          </span>

          <div class="lift-off__stage-body">
            <div class="lift-off__stage-row">
              <span class="lift-off__stage-label">{{ t(stage.labelKey) }}</span>
              <span
                v-if="stageStatus(i) === 'done'"
                class="lift-off__stage-chip lift-off__stage-chip--done"
              >
                <i class="far fa-check" />
                {{ t('completeV2.steps.liftOff.chip.done') }}
              </span>
              <span
                v-else-if="stageStatus(i) === 'active'"
                class="lift-off__stage-chip lift-off__stage-chip--active"
              >
                <span class="lift-off__stage-dot" />
                {{ t('completeV2.steps.liftOff.chip.active') }}
              </span>
              <span
                v-else
                class="lift-off__stage-chip lift-off__stage-chip--pending"
              >
                {{ t('completeV2.steps.liftOff.chip.pending') }}
              </span>
            </div>
            <p
              v-if="stage.descriptionKey"
              class="lift-off__stage-desc"
            >
              {{ t(stage.descriptionKey) }}
            </p>
            <div
              v-if="stageStatus(i) === 'active'"
              class="lift-off__stage-shimmer"
            />
          </div>
        </li>
      </ul>

      <!-- ── Écran "prêt" ── -->
      <transition name="ready-fade">
        <div
          v-if="ready"
          class="lift-off__ready"
        >
          <div class="lift-off__ready-glow" />

          <span class="lift-off__ready-badge">
            <i class="far fa-sparkles" />
            {{ t('completeV2.steps.liftOff.readyBadge') }}
          </span>

          <i class="far fa-rocket-launch lift-off__ready-icon" />

          <div class="lift-off__ready-title">
            {{ t('completeV2.steps.liftOff.readyTitle') }}
          </div>
          <p class="lift-off__ready-message">
            {{ t('completeV2.steps.liftOff.readyMessage') }}
          </p>

          <div class="lift-off__countdown">
            <div class="lift-off__countdown-head">
              <span class="lift-off__countdown-label">
                <i class="far fa-clock" />
                {{ t('completeV2.steps.liftOff.redirectingIn', { seconds: countdown }) }}
              </span>
            </div>
            <div class="lift-off__countdown-bar">
              <div
                class="lift-off__countdown-fill"
                :style="{ width: `${countdownPct}%` }"
              />
            </div>
          </div>

          <SpmButton
            :label="t('completeV2.steps.liftOff.discoverCta')"
            icon="far fa-rocket-launch"
            class-style="p-button p-button-success lift-off__ready-cta"
            @click="$emit('next')"
          />
        </div>
      </transition>

      <!-- ── Écran timeout "contactez-nous" ── -->
      <transition name="ready-fade">
        <div
          v-if="timedOut"
          class="lift-off__timeout"
        >
          <i class="far fa-hourglass-clock lift-off__timeout-icon" />
          <div class="lift-off__timeout-title">
            {{ t('completeV2.steps.liftOff.timeout.title') }}
          </div>
          <p
            class="lift-off__timeout-message"
            v-html="t('completeV2.steps.liftOff.timeout.message')"
          />
          <a
            class="lift-off__timeout-cta p-button p-button-outlined"
            href="mailto:support@shopimind.com"
          >
            <i class="far fa-envelope" />
            <span>{{ t('completeV2.steps.liftOff.timeout.contactCta') }}</span>
          </a>
        </div>
      </transition>
    </div>
  </StepShell>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  fetchProvisioningStatus,
  ProvisioningStage,
  startProvisioning,
} from '@/composables/onboarding-v2/useShopProvisioning';
import { UserState } from '@/composables/User';

const POLL_MS = 3_000;
const TIMEOUT_MS = 5 * 60 * 1_000;
const STAGNATION_MS = 100_000;
const AUTO_REDIRECT_S = 10;

const STAGES = [
  {
    id: 'env',
    labelKey: 'completeV2.steps.liftOff.stages.env',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.env',
    icon: 'far fa-server',
  },
  {
    id: 'db',
    labelKey: 'completeV2.steps.liftOff.stages.db',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.db',
    icon: 'far fa-database',
  },
  {
    id: 'sync',
    labelKey: 'completeV2.steps.liftOff.stages.sync',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.sync',
    icon: 'far fa-rotate',
  },
  {
    id: 'ready',
    labelKey: 'completeV2.steps.liftOff.stages.ready',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.ready',
    icon: 'far fa-rocket-launch',
  },
];

const STAGE_TO_INDEX: Record<ProvisioningStage, number> = {
  queued: 0,
  db_create: 0,
  db_connect: 1,
  migrate: 1,
  seed: 2,
  finalize: 2,
  done: 3,
};

export default defineComponent({
  name: 'LiftOffStep',

  components: {
    StepShell,
    SpmButton,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();
    const activeStage = ref(0);
    const ready = ref(false);
    const timedOut = ref(false);
    const countdown = ref(AUTO_REDIRECT_S);
    const countdownPct = computed(
      () => Math.max(0, (countdown.value / AUTO_REDIRECT_S) * 100),
    );

    const stages = computed(() => STAGES);

    const stageStatus = (i: number) => {
      if (ready.value) return 'done';
      if (i < activeStage.value) return 'done';
      if (i === activeStage.value) return 'active';
      return 'pending';
    };

    const applySnapshot = (stage: ProvisioningStage) => {
      activeStage.value = STAGE_TO_INDEX[stage] ?? 0;
      if (stage === 'done') ready.value = true;
    };

    let pollHandle: number | null = null;
    let countdownHandle: number | null = null;
    let timeoutHandle: number | null = null;
    let lastStage: ProvisioningStage | null = null;
    let lastStageChange = Date.now();

    const stopPoll = () => {
      if (pollHandle !== null) {
        window.clearInterval(pollHandle);
        pollHandle = null;
      }
    };

    const stopTimeout = () => {
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
    };

    const stopCountdown = () => {
      if (countdownHandle !== null) {
        window.clearInterval(countdownHandle);
        countdownHandle = null;
      }
    };

    watch(ready, (value) => {
      if (!value) return;
      countdown.value = AUTO_REDIRECT_S;
      countdownHandle = window.setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          stopCountdown();
          emit('next');
        }
      }, 1000);
    });

    onMounted(async () => {
      const shopId = UserState.activeShop?.id ?? 0;
      console.log(`[provisioning][LiftOffStep] mounted (shopId=${shopId})`);
      if (!shopId) {
        console.warn('[provisioning][LiftOffStep] no active shop — aborting');
        return;
      }

      const first = await startProvisioning(shopId);
      console.log(
        `[provisioning][LiftOffStep] initial snapshot stage=${first.stage} progress=${first.progress}`,
      );
      lastStage = first.stage;
      lastStageChange = Date.now();
      applySnapshot(first.stage);
      if (ready.value) {
        console.log(
          '[provisioning][LiftOffStep] already ready on mount — no polling needed',
        );
        return;
      }

      timeoutHandle = window.setTimeout(() => {
        if (!ready.value) {
          console.warn(
            `[provisioning][LiftOffStep] global TIMEOUT (${TIMEOUT_MS}ms) reached — switching to timeout screen`,
          );
          timedOut.value = true;
          stopPoll();
        }
      }, TIMEOUT_MS);

      pollHandle = window.setInterval(async () => {
        if (ready.value || timedOut.value) return;
        const snap = await fetchProvisioningStatus(shopId);
        applySnapshot(snap.stage);
        if (ready.value) {
          console.log(
            `[provisioning][LiftOffStep] reached DONE (stage=${snap.stage}) — stopping poll`,
          );
          stopPoll();
          stopTimeout();
          return;
        }
        if (snap.stage !== lastStage) {
          console.log(
            `[provisioning][LiftOffStep] stage changed: ${lastStage} → ${snap.stage}`,
          );
          lastStage = snap.stage;
          lastStageChange = Date.now();
          return;
        }
        const stagnationMs = Date.now() - lastStageChange;
        if (stagnationMs > STAGNATION_MS) {
          console.warn(
            `[provisioning][LiftOffStep] stagnation detected (stage="${snap.stage}" unchanged for ${stagnationMs}ms) — re-kicking provisioning`,
          );
          lastStageChange = Date.now();
          const relaunch = await startProvisioning(shopId);
          console.log(
            `[provisioning][LiftOffStep] re-kick returned stage=${relaunch.stage} progress=${relaunch.progress}`,
          );
          lastStage = relaunch.stage;
          applySnapshot(relaunch.stage);
          if (ready.value) {
            stopPoll();
            stopTimeout();
          }
        }
      }, POLL_MS);
    });

    onBeforeUnmount(() => {
      stopPoll();
      stopTimeout();
      stopCountdown();
    });

    return {
      t,
      stages,
      stageStatus,
      ready,
      timedOut,
      countdown,
      countdownPct,
    };
  },
});
</script>

<style lang="scss" scoped>
.lift-off {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  &--ready,
  &--timeout {
    flex: 1;
    justify-content: center;
    min-height: 100%;
  }

  /* ─── Stages ─── */
  &__stages {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  &__stage {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
    padding: 0.8rem 0.95rem;
    border: 1px solid #eef2ee;
    background: #ffffff;
    border-radius: 0.7rem;
    transition: all 0.3s ease;
    overflow: hidden;
  }

  &__stage--active {
    border-color: #94c840;
    background: linear-gradient(135deg, #f5faeb 0%, #ffffff 60%);
    box-shadow:
      0 4px 14px -6px rgba(148, 200, 64, 0.3),
      0 0 0 1px rgba(148, 200, 64, 0.15);
  }

  &__stage--done {
    background: #fafcfa;
    border-color: #e2eddf;
  }

  &__stage--pending {
    opacity: 0.72;
  }

  &__bullet {
    position: relative;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: #ffffff;
    border: 2px solid #e2e8f0;
    color: #94a3b8;
    font-size: 0.9rem;
    transition: all 0.3s ease;
  }

  &__stage--active &__bullet {
    border-color: #94c840;
    color: #5d8a24;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.15);
    animation: bullet-pulse 2s ease-in-out infinite;
  }

  &__stage--done &__bullet {
    border-color: #d4e7af;
    color: #5d8a24;
    background: #f5faeb;
    font-size: 0.85rem;
  }

  &__stage-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  &__stage-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
  }

  &__stage-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #475569;
    transition: color 0.3s ease;
  }

  &__stage--active &__stage-label {
    color: #0f172a;
    font-weight: 700;
  }

  &__stage--done &__stage-label {
    color: #2d5016;
  }

  &__stage-desc {
    margin: 0;
    font-size: 0.78rem;
    color: #94a3b8;
    line-height: 1.45;
  }

  &__stage--active &__stage-desc {
    color: #64748b;
  }

  &__stage-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.15rem 0.55rem;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    border-radius: 999px;
    flex-shrink: 0;

    i { font-size: 0.68rem; }

    &--done {
      background: rgba(148, 200, 64, 0.18);
      color: #5d8a24;
    }

    &--active {
      background: #ffffff;
      color: #5d8a24;
      border: 1px solid #94c840;
    }

    &--pending {
      background: #f1f5f9;
      color: #94a3b8;
    }
  }

  &__stage-dot {
    display: inline-block;
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    background: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.2);
    animation: dot-blink 1.4s ease-in-out infinite;
  }

  &__stage-shimmer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(148, 200, 64, 0.7),
      transparent
    );
    background-size: 40% 100%;
    animation: shimmer 1.6s linear infinite;
  }

  /* ─── Ready callout ─── */
  &__ready {
    position: relative;
    text-align: center;
    padding: 1.75rem 1.5rem 1.5rem;
    background: linear-gradient(135deg, #f5faeb 0%, #ffffff 60%);
    border: 1px solid #94c840;
    border-radius: 1rem;
    overflow: hidden;
    box-shadow:
      0 10px 30px -12px rgba(148, 200, 64, 0.25),
      0 2px 6px -2px rgba(15, 23, 42, 0.04);
  }

  &__ready-glow {
    position: absolute;
    top: -40%;
    left: 50%;
    transform: translateX(-50%);
    width: 320px;
    height: 320px;
    background: radial-gradient(circle, rgba(148, 200, 64, 0.3) 0%, transparent 65%);
    filter: blur(24px);
    pointer-events: none;
    animation: glow-pulse 3.5s ease-in-out infinite;
  }

  &__ready-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.7rem;
    margin-bottom: 0.8rem;
    background: linear-gradient(135deg, #94c840, #5d8a24);
    color: #ffffff;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-radius: 999px;
    box-shadow: 0 4px 10px -2px rgba(148, 200, 64, 0.4);

    i { font-size: 0.72rem; }
  }

  &__ready-icon {
    position: relative;
    display: block;
    font-size: 2.25rem;
    color: #5d8a24;
    margin-bottom: 0.65rem;
    animation: lift 2s ease-in-out infinite;
  }

  &__ready-title {
    position: relative;
    font-size: 1.25rem;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 0.35rem;
    letter-spacing: -0.01em;
  }

  &__ready-message {
    position: relative;
    margin: 0 auto 1.2rem;
    font-size: 0.88rem;
    color: #475569;
    line-height: 1.55;
    max-width: 26rem;
  }

  /* ─── Countdown ─── */
  &__countdown {
    position: relative;
    max-width: 26rem;
    margin: 0 auto;
    padding: 0.75rem 0.9rem;
    background: rgba(255, 255, 255, 0.65);
    border: 1px solid rgba(148, 200, 64, 0.3);
    border-radius: 0.6rem;
    backdrop-filter: blur(8px);
  }

  &__countdown-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.55rem;
  }

  &__countdown-label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #2d5016;

    i { font-size: 0.8rem; color: #5d8a24; }
  }

  &__countdown-bar {
    width: 100%;
    height: 4px;
    background: rgba(148, 200, 64, 0.15);
    border-radius: 999px;
    overflow: hidden;
  }

  &__countdown-fill {
    height: 100%;
    background: linear-gradient(to right, #94c840, #5d8a24);
    border-radius: 999px;
    transition: width 1s linear;
  }

  &__ready-cta {
    position: relative;
    margin-top: 1.25rem;
  }

  /* ─── Timeout callout ─── */
  &__timeout {
    text-align: center;
    padding: 2rem 1.5rem;
    background: #fffaf0;
    border: 1px solid #f3d9a3;
    border-radius: 1rem;
    max-width: 30rem;
    margin: 0 auto;
  }

  &__timeout-icon {
    display: block;
    font-size: 2rem;
    color: #b45309;
    margin-bottom: 0.85rem;
  }

  &__timeout-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 0.55rem;
  }

  &__timeout-message {
    margin: 0 0 1.25rem;
    font-size: 0.88rem;
    color: #475569;
    line-height: 1.6;

    :deep(strong) {
      color: #0f172a;
    }
  }

  &__timeout-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    text-decoration: none;
  }
}

:deep(.lift-off__ready-cta.p-button) {
  padding: 0.65rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 700;
}

@keyframes bullet-pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.15); }
  50% { box-shadow: 0 0 0 8px rgba(148, 200, 64, 0); }
}

@keyframes dot-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.85); }
}

@keyframes shimmer {
  0% { background-position: -40% 0; }
  100% { background-position: 140% 0; }
}

@keyframes lift {
  0%, 100% {
    transform: translateY(0) rotate(-5deg);
  }
  50% {
    transform: translateY(-6px) rotate(0deg);
  }
}

@keyframes glow-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.ready-fade-enter-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.ready-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
</style>
